import React, { useState } from "react";
import {
  Box,
  Typography,
  TextareaAutosize,
  Divider,
} from "@mui/material";
import PrimaryButton from "../components/Common/PrimaryButton.tsx";
import { ComponentConfig } from "../Config/ComponentConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { Colors } from "../Constants/Colors.js";
import StarRating from "../components/Rating/StarRating.tsx";
import Api from "../Interceptor/Api/index.ts";
import { ToastContainer, toast } from "react-toastify";
import { WebRoutes } from "../Routes/WebRoutes.js";
import HeaderWithBackButton from "../components/Common/HeaderWithBackButton.tsx";

// Define rating types
type RatingCriteria = {
  [key: string]: number;
};

const RatingComponent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state || {};
  const [ratings, setRatings] = useState<RatingCriteria>({
    overall_rating: 0,
    treatment_experience: 0,
    doctor_behavior: 0,
    doctor_on_time: 0,
    booking_journey_experience: 0,
  });
  const [feedbackSubmittingLoading, setFeedbackSubmittingLoading] =
    useState<boolean>(false);

  const [remarks, setRemarks] = useState<string>("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);

  const handleRatingChange = (key: string, rating: number) => {
    setRatings((prevRatings) => ({
      ...prevRatings,
      [key]: rating,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filteredRatings = Object.entries(ratings).reduce(
      (acc, [key, value]) => {
        if (key === "overall_rating" || value > 0) {
          acc[key] = value;
        }
        return acc;
      },
      {} as RatingCriteria
    );

    const feedbackData = {
      appointment_id: data?.appointmentId,
      feedback: {
        overall_rating: ratings.overall_rating,
        remarks: remarks,
        customer_id: data?.customerId,
        ratings: filteredRatings,
      },
    };
    try {
      setFeedbackSubmittingLoading(true);
      await Api.appointment.submitAppointmentFeedback(feedbackData);
      setFeedbackSubmitted(true);
      setTimeout(() => {
        navigate(WebRoutes.ALL_APPOINTMENTS, { replace: true });
      }, 2000);
    } catch (error) {
      toast.error(error?.message || "Error submitting feedback");
      console.error("Error submitting feedback:", error?.message);
    } finally {
      setFeedbackSubmittingLoading(false);
    }
  };

  const ratingFields = {
    mandatory: [{ key: "overall_rating", label: "Overall Rating" }],
    optional: [
      { key: "treatment_experience", label: "Treatment Experience" },
      { key: "doctor_behavior", label: "Doctor Behavior" },
      { key: "doctor_on_time", label: "Was Doctor On Time?" },
      {
        key: "booking_journey_experience",
        label: "Booking Journey Experience",
      },
    ],
  };

  React.useEffect(() => {
    const userId =
      localStorage.getItem("user_id") || sessionStorage.getItem("user_id");

    if (!userId) {
      navigate(WebRoutes.BOOKING_STEP_ONE);
    }
  }, [navigate]);

  return (
    <Box
      className="form-container-v1"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer />

      <HeaderWithBackButton title="Share your experience" />
      {feedbackSubmitted ? (
        <div style={{
          height: "60vh",
          display: "flex",
          margin: "0px 20px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }} className="success-container">
          {/* <Success /> */}
          <img
            style={{
              width: "60px",
              height: "60px",
            }}
            src="/icons/green_check_mark.svg"
            alt="Appointments"
          />
          <div>
            <p className="success-text-typo-1">Thanks!</p>
            <p className="success-text-typo-2">
              Your Feedback has been submitted successfully, you will be redirected shortly.
            </p>
          </div>
        </div>
      ) : (
        <Box
          display="flex"
          width="100%"
          flexDirection="column"
          alignItems="flex-start"
          padding={2}
          gap={2}
        >
          {ratingFields["mandatory"]?.map(({ key, label }) => {
            return (
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="subtitleBold" className="subtitle-bold">
                  {label}*
                </Typography>
                <StarRating
                  fieldKey={key}
                  rating={ratings[key]}
                  onChange={handleRatingChange}
                />
              </Box>
            );
          })}
          <Box width="100%">
            <Divider
              sx={{
                backgroundColor: Colors.greenBorderColor,
                height: "1px",
                marginBottom: 1,
              }}
            />

            <Typography variant="bodyRegular" className="body-regular">
              Rate Kyno Health on the following criteria
            </Typography>
          </Box>

          {ratingFields["optional"]?.map(({ key, label }) => (
            <Box display="flex" flexDirection="column" gap={1} key={key}>
              <Typography variant="subtitleBold" className="subtitle-bold">
                {label}
              </Typography>
              <StarRating
                fieldKey={key}
                rating={ratings[key]}
                onChange={handleRatingChange}
              />
            </Box>
          ))}

          {/* Remarks Section */}
          <Box width="100%">
            <Typography
              sx={{
                fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
                fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
              }}
              mb={1}
            >
              Remarks (optional)
            </Typography>
            <TextareaAutosize
              minRows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add remarks..."
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: ComponentConfig.ButtonBorderRadius,
                borderColor: Colors.grey,
                fontFamily: "inherit",
                outline: "none",
              }}
            />
            <Typography
              variant="caption"
              color={remarks.length > 100 ? "error" : "text.secondary"}
              align="right"
              display="block"
              mt={1}
            >
              {remarks.length}/100
            </Typography>
          </Box>

          {/* Submit Button */}
          <PrimaryButton
            onTap={handleSubmit}
            disabled={feedbackSubmittingLoading}
            textClassName="subtitle-medium"
            fullWidth
            text={feedbackSubmittingLoading ? "Submitting..." : "Submit"}
            sx={{
              background: ComponentConfig.primaryButtonGradient,
              color: "white",
              fontWeight: "bold",
              marginTop: 2,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default RatingComponent;
